import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { APP_CONFIG } from '@/config/app';
import { useAuth } from '@/hooks/useAuth';
import { OnlineContactsPanel } from '@/features/onlineContacts/OnlineContactsPanel';
import { metaService } from '@/services';
import {
  APP_SHELL_MAIN_GAP,
  APP_SHELL_MAIN_PT,
  APP_SHELL_PADDING_X,
  ONLINE_CONTACTS_ASIDE_ANCHOR,
  ONLINE_CONTACTS_COLUMN_CLASS,
  ONLINE_CONTACTS_COLUMN_CLASS_FOLDED,
  ONLINE_CONTACTS_MAIN_GAP,
  ONLINE_CONTACTS_PANEL_GUTTER,
  ONLINE_CONTACTS_PANEL_RIGHT_ALIGN,
  ONLINE_CONTACTS_PANEL_STICKY_TOP,
} from '@/styles/classes';
import { Sidebar } from './Sidebar';
import { PageHeader } from '@/components/ui';

interface AppLayoutProps {
  children: React.ReactNode;
  isStaff: boolean;
}

function AppFooter() {
  const { t } = useTranslation();
  const [backendVersion, setBackendVersion] = useState('unknown');

  useEffect(() => {
    let isMounted = true;

    metaService
      .getVersion()
      .then(data => {
        if (isMounted) {
          setBackendVersion(data.version || 'unknown');
        }
      })
      .catch(() => {
        if (isMounted) {
          setBackendVersion('unknown');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <footer className="flex min-h-12 shrink-0 items-center py-4">
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-[14px] leading-4 text-unilink-text-primary">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </p>
        <p className="text-[14px] leading-4 text-unilink-text-primary sm:text-right">
          {t('common.frontendVersion')}: {APP_CONFIG.VERSION} |{' '}
          {t('common.backendVersion')}: {backendVersion}
        </p>
      </div>
    </footer>
  );
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children, isStaff }) => {
  const { user } = useAuth();
  const [onlineContactsExpanded, setOnlineContactsExpanded] = useState(true);

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      <PageHeader />

      <div className={clsx('flex min-h-0 flex-1 flex-col', APP_SHELL_PADDING_X)}>
        <div className={clsx('flex min-h-0 flex-1', APP_SHELL_MAIN_GAP)}>
          <div className={clsx('flex min-h-0 shrink-0', APP_SHELL_MAIN_PT)}>
            <Sidebar isStaff={isStaff} />
          </div>
          <div
            className={clsx(
              'grid min-w-0 flex-1 items-start lg:grid-cols-[minmax(0,1fr)_auto]',
              user ? ONLINE_CONTACTS_MAIN_GAP : null
            )}
          >
            <main
              className={clsx(
                'min-w-0 flex flex-col',
                APP_SHELL_MAIN_PT
              )}
            >
              {children}
            </main>
            {user ? (
              <aside
                className={clsx(
                  onlineContactsExpanded
                    ? ONLINE_CONTACTS_COLUMN_CLASS
                    : ONLINE_CONTACTS_COLUMN_CLASS_FOLDED,
                  ONLINE_CONTACTS_ASIDE_ANCHOR,
                  ONLINE_CONTACTS_PANEL_GUTTER,
                  ONLINE_CONTACTS_PANEL_RIGHT_ALIGN,
                  ONLINE_CONTACTS_PANEL_STICKY_TOP
                )}
              >
                <OnlineContactsPanel
                  user={user}
                  expanded={onlineContactsExpanded}
                  onExpandedChange={setOnlineContactsExpanded}
                />
              </aside>
            ) : null}
          </div>
        </div>

        <AppFooter />
      </div>
    </div>
  );
};
